import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageFlag from '../common/LanguageFlag';
import { SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import { BRAND } from '../../theme/brandTokens';

const MultiLanguageTextArea = ({
  supportedLanguages = [],
  availableLanguages = [],
  values = {},
  onChange,
  errors,
  fieldName = 'about',
  label,
  labelContent,
  required = false,
  multiline = true,
  rows = 4,
  idPrefix = 'about'
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const formatFieldLabel = (text) => (required ? `${text} *` : text);

  const sortedLanguages = supportedLanguages
    .sort((a, b) => SUPPORTED_LANGUAGE_CODES.indexOf(a) - SUPPORTED_LANGUAGE_CODES.indexOf(b));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleValueChange = (langCode, value) => {
    onChange({
      ...values,
      [langCode]: value
    });
  };

  // Don't render if no supported languages
  if (!supportedLanguages || supportedLanguages.length === 0) {
    return null;
  }

  return (
    <>
      {label && (<Typography variant="h6" sx={{ color: BRAND.text, fontWeight: 600, mb: 2 }}>
        {formatFieldLabel(t(label))}
      </Typography>)}

      <Box id={`${idPrefix}-tabs-section`} sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          id={`${idPrefix}-tabs`}
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            backgroundColor: BRAND.background,
            '& .MuiTab-root': {
              color: BRAND.textMuted,
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: BRAND.accentText,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: BRAND.accent,
            },
          }}
        >
          {sortedLanguages.map((langCode, index) => {
            return (
              <Tab
                key={langCode}
                id={`${idPrefix}-tab-${langCode}`}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <LanguageFlag languageCode={langCode} size="1rem" />
                    <Typography variant="body2">{t(`common.languages.${langCode}`)}</Typography>
                  </Box>
                }
              />
            );
          })}
        </Tabs>
        <Box id={`${idPrefix}-tab-content-section`} sx={{ p: 3 }}>
          {sortedLanguages.map((langCode, index) => {
            return (
              <div
                key={langCode}
                id={`${idPrefix}-tab-content-${langCode}`}
                style={{ display: activeTab === index ? 'block' : 'none' }}
              >
                <TextField
                  id={`${idPrefix}-tab-content-textfield-${langCode}`}
                  fullWidth
                  label={`${t(labelContent)} (${t(`common.languages.${langCode}`)})${required ? ' *' : ''}`}
                  value={values[langCode] || ''}
                  onChange={(e) => handleValueChange(langCode, e.target.value)}
                  multiline={multiline}
                  rows={rows}
                  slotProps={{
                    input: {
                      style: { borderRadius: 12, fontSize: '1.1rem' },
                    }
                  }}
                />
              </div>
            );
          })}
        </Box>
      </Box>
      {errors && (
        <Alert severity="error" sx={{ mt: 1.5 }}>{t(errors)}</Alert>
      )}
    </>
  );
};

export default MultiLanguageTextArea;